/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import ContactRequests from './components/ContactRequests';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './components/Dashboard';
import ProjectDetails from './components/ProjectDetails';
import ErrorBoundary from './components/ErrorBoundary';
import AccessibilityStatement from './components/AccessibilityStatement';
import Projects from './components/Projects';
import TemplateLibrary from './components/TemplateLibrary';
import UserGuides from './components/UserGuides';
import UserManagement from './components/UserManagement';
import PlatformReports from './components/PlatformReports';
import PlatformSettings from './components/PlatformSettings';
import ProjectManagerDashboard from './components/ProjectManagerDashboard';
import SocialActorRegistry from './components/SocialActorRegistry';
import SocialInvestorRegistry from './components/SocialInvestorRegistry';
import SocialInvestmentRegistration from './components/SocialInvestmentRegistration';
import InvestorPortfolio from './components/InvestorPortfolio';
import InvestorInsights from './components/InvestorInsights';
import ParticipatoryPlanning from './components/ParticipatoryPlanning';
import CombinedInvestments from './components/CombinedInvestments';
import SupplyDemandMatching from './components/SupplyDemandMatching';
import CapacityBuilding from './components/CapacityBuilding';
import ImpactDashboard from './components/ImpactDashboard';
import QAOverview from './components/QAOverview';
import CertificationRenewal from './components/CertificationRenewal';
import InvestmentEvaluation from './components/InvestmentEvaluation';
import ContinuousImprovement from './components/ContinuousImprovement';
import PublicRegistry from './components/PublicRegistry';
import PMAnalytics from './components/PMAnalytics';
import PMTeamRoster from './components/PMTeamRoster';
import Meetings from './components/Meetings';
import Reimbursements from './components/Reimbursements';
import Financials from './components/Financials';
import BlendedPlanDetail from './components/BlendedPlanDetail';
import ProtectedModuleRoute from './components/ProtectedModuleRoute';
import PermissionsMatrix from './components/PermissionsMatrix';
import RestoreStatus from './components/RestoreStatus';
import HelpWidget from './components/HelpWidget';
import RegisterPage from './components/RegisterPage';
import PendingRegistrations from './components/PendingRegistrations';
import ChangePassword from './components/ChangePassword';
import { RBAC } from './lib/rbacMatrix';
import { Toaster } from './components/ui/sonner';
import { BACKEND_URL, API_URL } from './config';
import './App.css';

const API = API_URL;

// Set up axios interceptor for auth token
const setupAxiosInterceptors = () => {
  // Clear any existing interceptors
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();
  
  // Add request interceptor to handle auth tokens
  axios.interceptors.request.use(
    (config) => {
      // Don't add token to auth endpoints (login, register)
      if (!config.url.includes('/auth/login') && !config.url.includes('/auth/register')) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Add response interceptor to handle token expiration
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Only clear session if this is a protected endpoint and we have a token
        const currentToken = localStorage.getItem('token');
        const isProtectedEndpoint = !error.config?.url?.includes('/auth/');
        
        if (currentToken && isProtectedEndpoint) {
          console.log('Token expired, clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          // Use window.location.href instead of reload to avoid loops
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Watches the route and forces users with `must_change_password=true` onto
 * the mandatory `/change-password` screen — no matter which authenticated
 * URL they try to reach. Sits inside the router so it has access to
 * useLocation / useNavigate. Skips redirect for unauthenticated paths
 * (login, register, forgot-password, public landing & registry) so the
 * sign-in flow itself remains reachable.
 */
const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password',
  '/certified-actors', '/public-registry'];

const PasswordPolicyGate = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    if (!user.must_change_password) return;
    if (location.pathname === '/change-password') return;
    if (PUBLIC_PATHS.includes(location.pathname)) return;
    navigate('/change-password', { replace: true });
  }, [user, location.pathname, navigate]);
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize axios interceptors immediately
    setupAxiosInterceptors();
    
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('Restored user from localStorage:', userData);
        setUser(userData);
        
        // Set default auth header for axios
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } else {
      console.log('No stored authentication found');
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      console.log('Login attempt:', { username, API });
      
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Login failed:', errorData);
        return { 
          success: false, 
          error: errorData.detail || 'Login failed' 
        };
      }

      const responseData = await response.json();
      console.log('Login response:', responseData);
      
      const { access_token, user: userData } = responseData;
      
      if (!access_token || !userData) {
        console.error('Invalid response structure:', responseData);
        return { success: false, error: 'Invalid response from server' };
      }
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set axios default header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      
      console.log('Login successful, user set:', userData);
      return { success: true, must_change_password: !!userData.must_change_password };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Login failed' 
      };
    }
  };

  const register = async (username, email, password, role, organization_name = '') => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        username,
        email,
        password,
        role,
        organization_name
      });

      // Check if pending approval
      if (response.data.pending_approval) {
        return { 
          success: true, 
          pending_approval: true,
          message: response.data.message 
        };
      }

      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set axios default header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear axios default header
    delete axios.defaults.headers.common['Authorization'];
    
    setUser(null);
  };

  const initializeSampleData = async () => {
    try {
      await axios.post(`${API}/init-sample-data`);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to initialize sample data' 
      };
    }
  };

  // Mark the in-memory user as no longer requiring a password change.
  // Called by the ChangePassword screen after a successful update.
  const markPasswordChanged = () => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, must_change_password: false };
      try {
        localStorage.setItem('user', JSON.stringify(next));
      } catch (_) {
        /* ignore */
      }
      return next;
    });
  };

  // Helper to wrap any authenticated route. If the user is logged in but
  // still required to change their password, force them back to the
  // mandatory change-password screen — regardless of which URL they typed.
  const enforcePasswordPolicy = (element) => {
    if (user && user.must_change_password) {
      return <Navigate to="/change-password" replace />;
    }
    return element;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Skip to main content — WCAG 2.4.1 Bypass Blocks. Hidden until
          focused via Tab so it doesn't disturb the visual design. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-blue-700 focus:text-white focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-300"
        data-testid="skip-to-content-link"
      >
        Skip to main content
      </a>
      <BrowserRouter>
        <PasswordPolicyGate user={user} />
        <main id="main-content" tabIndex={-1}>
          <Routes>
          <Route 
            path="/login" 
            element={
              !user ? (
                <Login 
                  onLogin={login} 
                  onRegister={register}
                />
              ) : (
                <Navigate to="/dashboard" />
              )
            } 
          />
          <Route 
            path="/forgot-password" 
            element={<ForgotPassword />} 
          />
          <Route
            path="/change-password"
            element={
              user ? (
                <ChangePassword
                  user={user}
                  onLogout={logout}
                  onPasswordChanged={markPasswordChanged}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/register"
            element={!user ? <RegisterPage /> : <Navigate to="/dashboard" />}
          />
          
          {/* Public Registry - No authentication required */}
          <Route 
            path="/certified-actors" 
            element={<PublicRegistry />} 
          />
          <Route 
            path="/public-registry" 
            element={<PublicRegistry />} 
          />
          
          <Route 
            path="/dashboard" 
            element={
              user ? (
                enforcePasswordPolicy(<Dashboard user={user} onLogout={logout} />)
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/projects" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <Projects user={user} onLogout={logout} />
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/projects/:id" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <ErrorBoundary label="Project Details">
                  <ProjectDetails user={user} onLogout={logout} />
                </ErrorBoundary>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/templates" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <div className="container mx-auto px-4 py-8">
                    <TemplateLibrary user={user} />
                  </div>
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/guides" 
            element={
              user ? (
                <div className="min-h-screen bg-gray-50">
                  <div className="container mx-auto px-4 py-8">
                    <UserGuides user={user} />
                  </div>
                </div>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <div className="min-h-screen bg-gray-50">
                  <div className="container mx-auto px-4 py-8">
                    <UserManagement user={user} />
                  </div>
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/admin/reports" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <div className="min-h-screen bg-gray-50">
                  <div className="container mx-auto px-4 py-8">
                    <PlatformReports user={user} />
                  </div>
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <div className="min-h-screen bg-gray-50">
                  <div className="container mx-auto px-4 py-8">
                    <PlatformSettings user={user} />
                  </div>
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/social-actor-registry" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <SocialActorRegistry user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/social-investors" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <SocialInvestorRegistry user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
          {/* Public accessibility statement — must be reachable without auth
              per the EU Web Accessibility Directive monitoring methodology. */}
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route
            path="/admin/contact-requests"
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <ContactRequests />
              </ProtectedModuleRoute>
            }
          />

          <Route 
            path="/social-investment-registration" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ALL_ROLES}>
                <div className="min-h-screen bg-gray-50">
                  <SocialInvestmentRegistration user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route
            path="/investor/portfolio"
            element={
              <ProtectedModuleRoute
                user={user}
                allowedRoles={['investor', 'administrator', 'project_manager']}
              >
                <div className="min-h-screen bg-gray-50">
                  <InvestorPortfolio user={user} />
                </div>
              </ProtectedModuleRoute>
            }
          />
          <Route
            path="/investor/insights"
            element={
              <ProtectedModuleRoute
                user={user}
                allowedRoles={['investor', 'administrator', 'project_manager']}
              >
                <InvestorInsights user={user} />
              </ProtectedModuleRoute>
            }
          />
          <Route 
            path="/participatory-planning" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ALL_ROLES}>
                <div className="min-h-screen bg-gray-50">
                  <ParticipatoryPlanning user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/combined-investments" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <CombinedInvestments user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/supply-demand-matching" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <SupplyDemandMatching user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/capacity-building" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ALL_ROLES}>
                <div className="min-h-screen bg-gray-50">
                  <CapacityBuilding user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/impact-dashboard" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <ImpactDashboard user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/qa-overview" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <QAOverview user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/certification-renewal" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <CertificationRenewal user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/investment-evaluation" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.NON_GUEST}>
                <div className="min-h-screen bg-gray-50">
                  <InvestmentEvaluation user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route 
            path="/continuous-improvement" 
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_PM}>
                <div className="min-h-screen bg-gray-50">
                  <ContinuousImprovement user={user} onLogout={logout} />
                </div>
              </ProtectedModuleRoute>
            } 
          />
          <Route
            path="/admin/permissions"
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <div className="min-h-screen bg-gray-50">
                  <PermissionsMatrix user={user} />
                </div>
              </ProtectedModuleRoute>
            }
          />
          <Route
            path="/admin/restore-status"
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <div className="min-h-screen bg-gray-50">
                  <RestoreStatus user={user} />
                </div>
              </ProtectedModuleRoute>
            }
          />
          <Route
            path="/admin/pending-registrations"
            element={
              <ProtectedModuleRoute user={user} allowedRoles={RBAC.ADMIN_ONLY}>
                <div className="min-h-screen bg-gray-50">
                  <PendingRegistrations />
                </div>
              </ProtectedModuleRoute>
            }
          />
          <Route
            path="/pm/analytics"
            element={user ? <PMAnalytics user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/pm/team"
            element={user ? <PMTeamRoster user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/pm/meetings"
            element={user ? <Meetings user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/reimbursements"
            element={user ? <Reimbursements user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/financials"
            element={user ? <Financials user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/combined-investments/:id"
            element={user ? <BlendedPlanDetail user={user} /> : <Navigate to="/login" />}
          />
        </Routes>
        </main>
      </BrowserRouter>
      <Toaster />
      {user && <HelpWidget user={user} />}
    </div>
  );
}

export default App;